import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Modal from "../../components/Modal";

import { getUserDevicesByAgreementHub, deleteUserDevice } from "../../api/Agreement";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";

const UserDevicePage = () => {
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();

  const [devices, setDevices] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredDevices, setFilteredDevices] = useState([]);

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const modalRef = useRef();

  const handleOpenModal = () => {
    if (selectedDevices.length === 0) {
      toast.error("No devices selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleExportToExcel = () => {
    const devicesToExport =
      selectedDevices.length > 0
        ? filteredDevices.filter((device) =>
            selectedDevices.includes(device.userDevicePublicId)
          )
        : filteredDevices;

    if (devicesToExport.length === 0) {
      toast.error("No devices selected");
      return;
    }

    const devicesData = devicesToExport.map(
      ({
        userDevicePublicId,
        productUrl,
        displayName,
      }) => ({
        "Device id": userDevicePublicId,
        "Url": productUrl,
        Name: displayName,
      })
    );

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Users_${currentDate}`;

    handleExport({ Devices: devicesData }, filename);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    handleGetDevicesForAgreement();

    const handleUserDeviceUpdate = () => {
        handleGetDevicesForAgreement();
    };
    window.addEventListener("userDevicesUpdated", handleUserDeviceUpdate);

    return () => {
      window.removeEventListener("userDeviceUpdated", handleUserDeviceUpdate);
    };
  }, [agreementPublicId]);


  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = devices;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = devices.filter(
          (device) =>
            device.userId?.toLowerCase().includes(searchLower) ||
          device.displayName?.toLowerCase().includes(searchLower) ||
          device.productUrl?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredDevices(filtered);
    };

    applyFilterAndSort();
  }, [search, devices, sortColumn, sortOrder]);

  const handleGetDevicesForAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const devices = await getUserDevicesByAgreementHub(agreementPublicId, accessToken);

      setDevices(devices);
      setFilteredDevices(devices);
      setDevicesLoaded(true);
    } catch (error) {
      console.error(error);
      setDevicesLoaded(true);
    }
  };

  const handleDeleteUserDevice = async (userDevicePublicId) => {
    const toastId = toast.loading("Deleting user device...");
    try {
      const accessToken = await getAccessToken();
      await deleteUserDevice(userDevicePublicId, accessToken);
      const devices = await handleGetDevicesForAgreement(accessToken);
      setDevices(devices);

      toast.success("User device deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      toast.error("Failed to delete user device", { id: toastId });
      console.error(error);
    }
  };

  console.log("Devices:", devices);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"User device"} loading={!devicesLoaded} />
            </div>

            <div className={`${!devicesLoaded ? "loading" : ""} page-content pb-[25rem]`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">User devices</h2>

                <Link
                  to={`/${agreementPublicId}/create-user-device`}
                  className="btn-primary"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create user device
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn btn-outline-primary-small hidden"
                    onClick={handleOpenModal}
                    disabled={selectedDevices.length === 0}
                  >
                    Edit
                  </button>
                  <div className="flex items-center">

                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Url"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4 mt-2"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredDevices}
                    selectedRows={selectedDevices} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedDevices} // Ensure selection updates correctly
                    noCheckbox={true}
                    columns={[
                      { key: "displayName", label: "Name" },
                      { key: "productUrl", label: "Device url" },
                    ]}
                    primaryKey="userDevicePublicId" // Match this with userDevicePublicId
                    basePath={`/${agreementPublicId}/user-devices`}
                    handleDelete={handleDeleteUserDevice}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">
            Change billing group / account type
          </h2>
{/*           <div className="flex flex-col gap-4">
            <Dropdown
              label="Select billing group"
              options={billingGroups.map((group) => group.name)}
              value={
                billingGroups.find(
                  (group) => group.billingGroupPublicId === selectedBillingGroup
                )?.name || ""
              }
              onSelect={handleSelectBillingGroup}
            />

            <Dropdown
              label="Select account type"
              options={accountTypes.map((at) => at.name)}
              value={
                accountTypes.find(
                  (at) => at.accountTypePublicId === selectedAccountType
                )?.name || ""
              }
              onSelect={handleSelectAccountType}
            />
          </div>

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
             <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button> 
          </div> */}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default UserDevicePage;
